import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArcElement, Chart, Legend, Tooltip } from 'chart.js';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Doughnut } from 'react-chartjs-2';
import { useFicFidcDashboardDate } from 'src/context/FicFidcDashboardDateContext';
import ipanema from 'src/graphql/clients/ipanema';
import {
  GET_FIDC_UNDERLYING_ASSETS_AND_MANAGERS,
  GET_FIDC_UNDERLYING_ASSETS_AND_MANAGERS_WITH_COMPETENCE,
} from 'src/graphql/queries';
import { MetaCarteirasFicFidcsChildPosition } from 'src/services/meta-carteiras-fic-fidcs';
import { FIC_FIDC_CHART_COLORS } from 'src/utils/ficFidcChartColors';
import { convertValue, formatPercentageValue } from 'src/utils/number';

import {
  FicFidcCompositionItem,
  FidcUnderlyingAssetsAndManagers,
  mapFicFidcComposition,
} from './mapFicFidcComposition';

Chart.register(ArcElement, Legend, Tooltip);

interface FicFidcCompositionChartsProps {
  baseMonth?: string;
  positions: MetaCarteirasFicFidcsChildPosition[];
  hasPermission: boolean;
}

interface UnderlyingAssetsAndManagersQueryData {
  getFidcUnderlyingAssetsAndManagers: FidcUnderlyingAssetsAndManagers[];
}

interface CompositionChartCardProps {
  title: string;
  items: FicFidcCompositionItem[];
}

interface CompositionLegendProps {
  colors: string[];
  items: FicFidcCompositionItem[];
}

function getDisplayItems(items: FicFidcCompositionItem[]) {
  if (items.length <= 5) return items;

  const mainItems = items.slice(0, 5);
  const otherItems = items.slice(5);
  const value = otherItems.reduce((sum, item) => sum + item.value, 0);

  return [
    ...mainItems,
    {
      key: 'others',
      label: 'Outros',
      value,
      percentage: otherItems.reduce((sum, item) => sum + item.percentage, 0),
    },
  ];
}

function formatCompactCurrency(value: number) {
  return convertValue(value, '', true, 'currency', 'BRL', 1, 0);
}

function formatBaseMonth(baseMonth?: string) {
  if (!baseMonth) return undefined;

  try {
    return format(parseISO(baseMonth), 'MM/yyyy', { locale: ptBR });
  } catch {
    return undefined;
  }
}

function CompositionLegend({ colors, items }: CompositionLegendProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        gridTemplateColumns: 'minmax(0, 1fr) auto auto',
        width: '100%',
      }}>
      <Typography variant="caption">CLASSE</Typography>
      <Typography align="right" variant="caption">
        MONTANTE
      </Typography>
      <Typography align="right" variant="caption">
        % POSIÇÃO
      </Typography>
      {items.map((item, index) => (
        <Box
          key={item.key}
          sx={{
            alignItems: 'center',
            display: 'contents',
          }}>
          <Box
            sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                backgroundColor: colors[index],
                borderRadius: '50%',
                flexShrink: 0,
                height: 10,
                width: 10,
              }}
            />
            <Typography noWrap variant="body2">
              {item.label}
            </Typography>
          </Box>
          <Typography
            align="right"
            sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            variant="body2">
            {formatCompactCurrency(item.value)}
          </Typography>
          <Typography
            align="right"
            sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            variant="body2">
            {formatPercentageValue(item.percentage, 1)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CompositionChartCard({ title, items }: CompositionChartCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dialogTitleId = `${title
    .toLocaleLowerCase('pt-BR')
    .replace(/\s+/g, '-')}-dialog-title`;
  const displayItems = useMemo(() => getDisplayItems(items), [items]);
  const displayColors = displayItems.map(
    (_, index) => FIC_FIDC_CHART_COLORS[index % FIC_FIDC_CHART_COLORS.length],
  );
  const allColors = items.map(
    (_, index) => FIC_FIDC_CHART_COLORS[index % FIC_FIDC_CHART_COLORS.length],
  );

  const chartData = (
    chartItems: FicFidcCompositionItem[],
    colors: string[],
  ) => ({
    labels: chartItems.map((item) => item.label),
    datasets: [
      {
        data: chartItems.map((item) => item.value),
        backgroundColor: colors,
        borderWidth: 0,
      },
    ],
  });

  const getChartOptions = (chartItems: FicFidcCompositionItem[]) => ({
    cutout: '70%',
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: {
            dataIndex: number;
            label: string;
            parsed: number;
          }) => {
            const item = chartItems[context.dataIndex];
            const percentage = item?.percentage ?? 0;

            return `${context.label}: ${formatPercentageValue(percentage, 1)} (${formatCompactCurrency(context.parsed)})`;
          },
        },
      },
    },
  });

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardActionArea
          aria-label={`Ver detalhamento de ${title}`}
          onClick={() => setIsOpen(true)}
          sx={{ height: '100%' }}>
          <CardContent>
            <Typography component="h3" variant="h6">
              {title}
            </Typography>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mt: 2,
              }}>
              <Box
                sx={{
                  flexShrink: 0,
                  height: 180,
                  position: 'relative',
                  width: 180,
                }}>
                <Doughnut
                  data={chartData(displayItems, displayColors)}
                  options={getChartOptions(displayItems)}
                />
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    inset: 0,
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    position: 'absolute',
                  }}>
                  <Typography fontSize={32} fontWeight={700} lineHeight={1}>
                    {items.length}
                  </Typography>
                </Box>
              </Box>
              <CompositionLegend colors={displayColors} items={displayItems} />
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
      <Dialog
        aria-labelledby={dialogTitleId}
        fullWidth
        maxWidth="md"
        onClose={() => setIsOpen(false)}
        open={isOpen}>
        <DialogTitle id={dialogTitleId} sx={{ pr: 7 }}>
          {title}
          <IconButton
            aria-label="Fechar"
            onClick={() => setIsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 360, mb: 3 }}>
            <Doughnut
              data={chartData(items, allColors)}
              options={getChartOptions(items)}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Classe</TableCell>
                  <TableCell align="right">Montante</TableCell>
                  <TableCell align="right">% da posição</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.key}>
                    <TableCell>
                      <Box
                        sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                        <Box
                          sx={{
                            backgroundColor: allColors[index],
                            borderRadius: '50%',
                            flexShrink: 0,
                            height: 10,
                            width: 10,
                          }}
                        />
                        {item.label}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatCompactCurrency(item.value)}
                    </TableCell>
                    <TableCell align="right">
                      {formatPercentageValue(item.percentage, 1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function FicFidcCompositionCharts({
  baseMonth,
  positions,
  hasPermission,
}: FicFidcCompositionChartsProps) {
  const formattedBaseMonth = formatBaseMonth(baseMonth);
  // Fora do dashboard FIC/FIDC (sem provider) e sem competência selecionada,
  // a query usa o documento legado, sem month/year, e não depende do schema
  // novo do Ipanema (UQ-4510); com date autoritativa, o documento estendido é
  // usado e a competência é convertida, garantindo cache/refetch distintos
  // por variables.
  const dashboardDate = useFicFidcDashboardDate();
  const dashboardDateValue = dashboardDate?.date ?? null;
  const hashes = useMemo(
    () =>
      Array.from(
        new Set(
          positions.map((position) => position.operationId).filter(Boolean),
        ),
      ),
    [positions],
  );
  const competence = useMemo(() => {
    if (!dashboardDateValue) return undefined;

    try {
      const parsed = parseISO(dashboardDateValue);

      return {
        month: Number(format(parsed, 'M')),
        year: Number(format(parsed, 'yyyy')),
      };
    } catch {
      return undefined;
    }
  }, [dashboardDateValue]);
  const queryVariables = useMemo(
    () => (competence ? { hashes, ...competence } : { hashes }),
    [competence, hashes],
  );
  const { data, error, loading } =
    useQuery<UnderlyingAssetsAndManagersQueryData>(
      competence
        ? GET_FIDC_UNDERLYING_ASSETS_AND_MANAGERS_WITH_COMPETENCE
        : GET_FIDC_UNDERLYING_ASSETS_AND_MANAGERS,
      {
        client: ipanema,
        skip: !hasPermission || !hashes.length,
        variables: queryVariables,
      },
    );
  const metadataOperationIds = useMemo(
    () =>
      new Set(
        data?.getFidcUnderlyingAssetsAndManagers.map((item) =>
          String(item.operationId),
        ),
      ),
    [data],
  );
  const hasMissingCompositionMetadata = hashes.some(
    (hash) => !metadataOperationIds.has(String(hash)),
  );
  const isCompositionMetadataReady =
    !hashes.length ||
    (!loading &&
      !error &&
      data !== undefined &&
      !hasMissingCompositionMetadata);
  const composition = useMemo(
    () =>
      mapFicFidcComposition(
        positions,
        data?.getFidcUnderlyingAssetsAndManagers ?? [],
      ),
    [data, positions],
  );

  if (
    !hasPermission ||
    !isCompositionMetadataReady ||
    !composition.assets.length ||
    !composition.managers.length
  ) {
    return null;
  }

  return (
    <Box mt={3}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 0.5,
          justifyContent: 'flex-end',
          mb: 1,
        }}>
        {formattedBaseMonth && (
          <Typography fontWeight={700} variant="body2">
            Mês base: {formattedBaseMonth}
          </Typography>
        )}
        <MuiTooltip title="Os percentuais consideram o valor da posição de cada fundo na carteira, e não uma divisão simples entre as categorias.">
          <IconButton
            aria-label="Como os percentuais são calculados"
            size="small">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      </Box>
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <CompositionChartCard
            title="Composição por Ativo-lastro"
            items={composition.assets}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <CompositionChartCard
            title="Composição por Gestor"
            items={composition.managers}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
